import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, IconButton, Paper, Typography, TextField, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AgentMessage from './AgentMessage';
import AgentActivityCard from './AgentActivityCard';
import AgentConfirmCard from './AgentConfirmCard';
import AgentUpdateConfirmCard from './AgentUpdateConfirmCard';
import AgentWizardInput from './AgentWizardInput';
import type { UseAgentReturn } from '../hooks/useAgent';

interface Props {
  agent: UseAgentReturn;
  onClose: () => void;
  /** モバイル等で全画面表示したい場合に true。位置・サイズが画面いっぱいに切り替わる */
  fullscreen?: boolean;
}

export default function AgentPanel({ agent, onClose, fullscreen = false }: Props) {
  const { uiMessages, status, currentStep, wizard, send, resetConversation, resolveConfirm, answerWizard } = agent;
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [uiMessages, currentStep, scrollToBottom]);

  const canSend = status === 'idle' || status === 'error';
  const isAwaitingConfirm = status === 'awaiting_confirm';
  const isAwaitingWizard = status === 'awaiting_wizard' && !!wizard;

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    await send(text);
  }, [input, send]);

  // 表示モードに応じた Paper の sx を構築
  const paperSx = fullscreen
    ? {
        position: 'fixed' as const,
        inset: 0,
        width: '100vw',
        height: '100vh',
        // iOS の動的 viewport / セーフエリア対応
        // dvh が使えるブラウザではそちらを優先
        ['@supports (height: 100dvh)']: { height: '100dvh' },
        display: 'flex' as const,
        flexDirection: 'column' as const,
        zIndex: 10500,
        borderRadius: 0,
        overflow: 'hidden' as const,
        // iPhone のセーフエリアぶんは内側の Box が padding で吸収する
      }
    : {
        position: 'fixed' as const,
        right: 16,
        top: 80,
        width: 420,
        maxWidth: 'calc(100vw - 32px)',
        height: '70vh',
        minHeight: 500,
        maxHeight: 'calc(100vh - 100px)',
        display: 'flex' as const,
        flexDirection: 'column' as const,
        zIndex: 10500,
        borderRadius: 2,
        overflow: 'hidden' as const,
        border: '1px solid #e0e7ff',
      };

  return (
    <Paper elevation={10} sx={paperSx}>
      {/* ヘッダー */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          // フルスクリーン表示時は iPhone 等のセーフエリア（ノッチ）ぶん上端に余白
          ...(fullscreen && { pt: 'calc(8px + env(safe-area-inset-top, 0px))' }),
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: '#fff',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <SmartToyIcon sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={800}>
            エージェント
          </Typography>
        </Box>
        <Box display="flex" gap={0.5}>
          <Tooltip title="新規会話">
            <IconButton size="small" onClick={resetConversation} sx={{ color: '#fff' }}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="閉じる">
            <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* メッセージリスト */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          background: '#fafafa',
          py: 1,
        }}
      >
        {uiMessages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <SmartToyIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              何か手伝えることはありますか？
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              例: 「田中花子さんを教えて」<br />
              「7/10 10:00 - 7/12 18:00、アルファードで予約して」
            </Typography>
          </Box>
        )}

        {uiMessages.map((m) => {
          if (m.kind === 'user') return <AgentMessage key={m.id} role="user" text={m.text} />;
          if (m.kind === 'assistant') return <AgentMessage key={m.id} role="assistant" text={m.text} />;
          if (m.kind === 'error') return <AgentMessage key={m.id} role="error" text={m.text} />;
          if (m.kind === 'activity') {
            return (
              <AgentActivityCard
                key={m.id}
                toolName={m.toolName}
                args={m.args}
                status={m.status}
                result={m.result}
                errorMessage={m.errorMessage}
                durationMs={m.durationMs}
              />
            );
          }
          if (m.kind === 'confirm') {
            return (
              <AgentConfirmCard
                key={m.id}
                args={m.args}
                state={m.state}
                onResolve={m.state === 'pending' ? resolveConfirm : undefined}
              />
            );
          }
          if (m.kind === 'update_confirm') {
            return (
              <AgentUpdateConfirmCard
                key={m.id}
                before={m.before}
                changes={m.changes}
                state={m.state}
                onResolve={m.state === 'pending' ? resolveConfirm : undefined}
              />
            );
          }
          return null;
        })}

        {currentStep && !isAwaitingConfirm && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {currentStep}
            </Typography>
          </Box>
        )}
      </Box>

      {/* 入力欄（ウィザード時はウィザード入力に置換） */}
      {isAwaitingWizard && wizard ? (
        <Box sx={fullscreen ? { pb: 'env(safe-area-inset-bottom, 0px)' } : undefined}>
          <AgentWizardInput wizard={wizard} onAnswer={answerWizard} />
        </Box>
      ) : (
        <Box sx={{
          borderTop: '1px solid #e2e8f0',
          p: 1,
          background: '#fff',
          // フルスクリーン表示時は下端のホームバー領域ぶん余白
          ...(fullscreen && { pb: 'calc(8px + env(safe-area-inset-bottom, 0px))' }),
        }}>
          <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder={
                isAwaitingConfirm
                  ? '確認カードのボタンで応答してください…'
                  : canSend
                    ? 'メッセージを入力…'
                    : '応答中…'
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // IME変換中の Enter は送信しない（日本語変換の確定を誤爆しない）
                const ne: any = e.nativeEvent;
                if (ne?.isComposing || ne?.keyCode === 229) return;
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={!canSend || isAwaitingConfirm}
              // iOS Safari は input の font-size が 16px 未満だとフォーカス時に自動ズームする。
              // PC とモバイルで共通コンポーネントを保ちたいので、両者とも 16px に揃える。
              inputProps={{ style: { fontSize: 16 } }}
            />
            <IconButton
              color="primary"
              disabled={!canSend || !input.trim() || isAwaitingConfirm}
              onClick={handleSend}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
